// keyboard_tracking: the phone pill's fold and unfold paced by the keyboard's
// own slide (lib/keyboardEdgeAdapter's samples) instead of a clock of their
// own. The pill rides the keyboard's edge already; with its height run off the
// same samples it opens as the keyboard rises and is folded as the keyboard
// lands — one motion, on the keyboard's curve, ending with it.
//
// The resize starts as an ordinary Web Animation (NewAnswer's height FLIP),
// since the press that starts it can come before the keyboard moves, and with
// no keyboard at all it simply plays out. Once a slide in its direction is
// under way the animation is taken over: paused, made linear, and its time set
// from the slide's progress on every sample. It never goes back — the box
// holds where its own clock had got to until the keyboard passes it — and it
// finishes when the keyboard lands, turns, or stops reporting.

import type { KeyboardSlide } from 'lib/keyboardEdgeAdapter'

// The part of a Web Animation this uses.
export type SlideResizeAnimation = {
    playState: string
    currentTime: unknown
    pause(): void
    finish(): void
    effect: { updateTiming?(timing: { easing?: string }): void } | null
}

export function resolveSlideResizeFraction({
    fromHeight,
    toHeight,
    currentHeight,
}: {
    fromHeight: number
    toHeight: number
    currentHeight: number
}): number {
    const span = Number(toHeight) - Number(fromHeight)
    if (!Number.isFinite(span) || Math.abs(span) < 1) return 1
    const fraction = (Number(currentHeight) - Number(fromHeight)) / span
    return Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0
}

export function followKeyboardSlide({
    animation,
    duration,
    direction,
    fromHeight,
    toHeight,
    measureHeight,
    slide,
    subscribe,
}: {
    animation: SlideResizeAnimation
    duration: number
    // The slide that paces this resize: an unfold goes with an opening
    // keyboard, a fold with a closing one.
    direction: KeyboardSlide['direction']
    fromHeight: number
    toHeight: number
    // The box's height as drawn now, the running animation applied.
    measureHeight(): number
    slide(): KeyboardSlide | null
    subscribe(listener: () => void): () => void
}): () => void {
    let following = false
    let fraction = 0
    let unsubscribe: (() => void) | null = null

    const stop = () => {
        unsubscribe?.()
        unsubscribe = null
    }

    const update = () => {
        if (!unsubscribe) return
        if (animation.playState === 'finished' || animation.playState === 'idle') {
            stop()
            return
        }
        const current = slide()
        const paced = current?.direction === direction && current.progress != null
        if (!following) {
            if (!paced) return
            fraction = resolveSlideResizeFraction({
                fromHeight,
                toHeight,
                currentHeight: measureHeight(),
            })
            animation.pause()
            animation.effect?.updateTiming?.({ easing: 'linear' })
            following = true
        } else if (!paced) {
            // Landed, turned around, or handed back: the box takes its end.
            stop()
            animation.finish()
            return
        }
        fraction = Math.max(fraction, current!.progress as number)
        if (fraction >= 1) {
            stop()
            animation.finish()
            return
        }
        animation.currentTime = fraction * duration
    }

    unsubscribe = subscribe(update)
    update()
    return stop
}
